import { useEffect, useMemo, useRef, useState } from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Fab from "@mui/material/Fab";
import Icon from "@mui/material/Icon";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import { alpha } from "@mui/material/styles";

import { post } from "lib/api";
import { getAiActions, getAiContext } from "lib/aiContext";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useAIAssistant } from "context/AIAssistantContext";

const makeMsg = (role, content) => ({ role, content: String(content ?? "") });

const renderMessageContent = (content) => {
  const text = String(content ?? "").trim();
  if (!text) return null;

  const sections = text.split(/\n{2,}/).filter(Boolean);
  return (
    <Stack spacing={1.1}>
      {sections.map((section, sectionIdx) => {
        const lines = section
          .split("\n")
          .map((line) => line.trimEnd())
          .filter((line) => line.trim().length);
        const isBulletList = lines.length > 0 && lines.every((line) => /^([-*]|\d+\.)\s+/.test(line.trim()));

        if (isBulletList) {
          return (
            <Stack key={`section_${sectionIdx}`} spacing={0.75}>
              {lines.map((line, lineIdx) => (
                <Box key={`line_${lineIdx}`} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      width: 5,
                      height: 5,
                      borderRadius: "50%",
                      mt: "9px",
                      flexShrink: 0,
                      bgcolor: "currentColor",
                      opacity: 0.62,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontSize: 14, lineHeight: 1.65, overflowWrap: "anywhere", wordBreak: "break-word" }}
                  >
                    {line.replace(/^([-*]|\d+\.)\s+/, "")}
                  </Typography>
                </Box>
              ))}
            </Stack>
          );
        }

        return (
          <Typography
            key={`section_${sectionIdx}`}
            variant="body2"
            sx={{ fontSize: 14, whiteSpace: "pre-wrap", lineHeight: 1.65, overflowWrap: "anywhere", wordBreak: "break-word" }}
          >
            {lines.join("\n")}
          </Typography>
        );
      })}
    </Stack>
  );
};

const inferModeFromContext = (ctx) => {
  const screen = String(ctx?.screen || "").trim().toLowerCase();
  if (screen === "posting_rules") return "posting_rules";
  if (screen === "build_sheet") return "build_sheet";
  return null;
};

export default function AIAssistantDrawer() {
  const {
    open,
    setOpen,
    messages,
    setMessages,
    previousResponseId,
    setPreviousResponseId,
    proposedPatch,
    setProposedPatch,
    resetThread,
    prefill,
    consumePrefill,
  } = useAIAssistant();

  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [proposing, setProposing] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [useWebSearch, setUseWebSearch] = useState(false);
  const [hasDialogOpen, setHasDialogOpen] = useState(false);
  const bottomRef = useRef(null);

  const uiContext = getAiContext();
  const mode = inferModeFromContext(uiContext);

  const canApplyHere = useMemo(() => {
    const actions = getAiActions() || {};
    if (!proposedPatch) return false;
    if (mode === "posting_rules") return typeof actions.applyPostingRulePatchV1 === "function";
    if (mode === "build_sheet") return typeof actions.applyBuildSheetPatchV2 === "function";
    return false;
  }, [proposedPatch, mode]);

  const scrollToBottom = () => {
    try {
      bottomRef.current?.scrollIntoView?.({ behavior: "smooth", block: "end" });
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(scrollToBottom, 0);
    return () => window.clearTimeout(t);
  }, [open, messages.length, proposedPatch]);

  useEffect(() => {
    if (!open) return;
    if (typeof prefill === "string" && prefill.trim()) {
      setInput(prefill);
      consumePrefill();
    }
  }, [open, prefill, consumePrefill]);

  useEffect(() => {
    const onKey = (e) => {
      if (!e) return;
      // Ctrl+Shift+A to toggle the assistant, even inside dialogs.
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && String(e.key || "").toLowerCase() === "a") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const recompute = () => {
      try {
        const anyMuiDialog = Boolean(document.querySelector(".MuiDialog-root"));
        const anyRoleDialog = Boolean(document.querySelector('[role="dialog"]'));
        setHasDialogOpen(anyMuiDialog || anyRoleDialog);
      } catch {
        setHasDialogOpen(false);
      }
    };

    recompute();
    const obs = new MutationObserver(() => recompute());
    obs.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("focus", recompute);
    window.addEventListener("resize", recompute);
    return () => {
      obs.disconnect();
      window.removeEventListener("focus", recompute);
      window.removeEventListener("resize", recompute);
    };
  }, []);

  const send = async () => {
    const q = String(input || "").trim();
    if (!q || loading || proposing) return;
    setErrorText("");
    setLoading(true);
    setProposedPatch(null);

    setMessages((prev) => [...prev, makeMsg("user", q)]);
    setInput("");

    try {
      const res = await post("/api/assistant/ask", {
        question: q,
        previousResponseId,
        context: getAiContext(),
      });
      if (res?.responseId) setPreviousResponseId(res.responseId);
      setMessages((prev) => [...prev, makeMsg("assistant", res?.answer || "No response.")]);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("AI ask failed:", e);
      setErrorText("AI request failed. Check backend logs / OpenAI key.");
      setMessages((prev) => [...prev, makeMsg("assistant", "AI request failed.")]);
    } finally {
      setLoading(false);
    }
  };

  const propose = async () => {
    const q = String(input || "").trim();
    if (!q || loading || proposing) return;
    if (!mode) {
      setErrorText("No patch mode for this screen. Go to Posting Rules or a Build Sheet editor to propose patches.");
      return;
    }
    setErrorText("");
    setProposing(true);
    setProposedPatch(null);

    setMessages((prev) => [...prev, makeMsg("user", q)]);
    setInput("");

    try {
      const res = await post("/api/assistant/propose", {
        mode,
        question: q,
        previousResponseId,
        context: getAiContext(),
        ...(mode === "build_sheet" ? { useWebSearch } : {}),
      });
      if (res?.responseId) setPreviousResponseId(res.responseId);
      if (res?.patch) {
        setProposedPatch(res.patch);
        setMessages((prev) => [
          ...prev,
          makeMsg("assistant", `Proposed changes ready:\n${res.patch.summary || ""}\n\nReview below, then Apply here.`),
        ]);
      } else {
        setMessages((prev) => [...prev, makeMsg("assistant", "No patch returned.")]);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("AI propose failed:", e);
      setErrorText("AI propose failed. Check backend logs.");
      setMessages((prev) => [...prev, makeMsg("assistant", "AI propose failed.")]);
    } finally {
      setProposing(false);
    }
  };

  const applyHere = () => {
    if (!proposedPatch) return;
    const actions = getAiActions() || {};
    try {
      if (mode === "posting_rules" && typeof actions.applyPostingRulePatchV1 === "function") actions.applyPostingRulePatchV1(proposedPatch);
      else if (mode === "build_sheet" && typeof actions.applyBuildSheetPatchV2 === "function") actions.applyBuildSheetPatchV2(proposedPatch);
      else setErrorText("No apply handler registered for this screen/editor.");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Apply patch failed:", e);
      setErrorText("Apply failed (see console).");
    }
  };

  const drawerZ = (theme) => (theme?.zIndex?.modal ?? 1300) + 5;
  const contextLabel = (String(uiContext?.screen || mode || "generic")).replaceAll("_", " ");
  const routeLabel = uiContext?.route?.pathname ? uiContext.route.pathname : "";
  const promptEmpty = !(String(input || "").trim());
  const actionButtonSx = {
    minWidth: 0,
    px: 1.4,
    py: 0.9,
    borderRadius: 999,
    textTransform: "none",
    fontWeight: 600,
    fontSize: 13,
    lineHeight: 1.2,
    letterSpacing: 0,
    boxShadow: "none",
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ zIndex: drawerZ }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 460 },
            zIndex: drawerZ,
            overflow: "hidden",
            color: "common.white",
            borderLeft: "1px solid rgba(255,255,255,0.08)",
            background:
              "radial-gradient(circle at top, rgba(88, 160, 255, 0.18), transparent 32%), linear-gradient(180deg, #111827 0%, #0b1220 52%, #09101a 100%)",
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            background: "linear-gradient(180deg, rgba(255,255,255,0.035), rgba(255,255,255,0.01))",
            backdropFilter: "blur(14px)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
            <Box sx={{ display: "flex", gap: 1, minWidth: 0, alignItems: "center" }}>
              <Avatar
                sx={{
                  width: 30,
                  height: 30,
                  bgcolor: "rgba(87, 140, 255, 0.12)",
                  color: "#d7e6ff",
                  border: "1px solid rgba(133, 176, 255, 0.16)",
                  boxShadow: "0 8px 20px rgba(22, 40, 84, 0.22)",
                }}
              >
                <Icon sx={{ fontSize: 15 }}>auto_awesome</Icon>
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.2,
                    fontWeight: 600,
                    letterSpacing: "-0.01em",
                    color: "rgba(255,255,255,0.92)",
                  }}
                >
                  AI Assistant
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.2,
                    color: "rgba(255,255,255,0.56)",
                    fontSize: 11,
                    lineHeight: 1.35,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {contextLabel}
                  {routeLabel ? ` • ${routeLabel}` : ""}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 0.75, flexShrink: 0 }}>
              <IconButton
                size="small"
                onClick={resetThread}
                title="New thread"
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: "rgba(255,255,255,0.04)",
                  color: "rgba(255,255,255,0.82)",
                  border: "1px solid rgba(255,255,255,0.07)",
                }}
              >
                <Icon fontSize="small">refresh</Icon>
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setOpen(false)}
                title="Close"
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: "rgba(255,255,255,0.04)",
                  color: "rgba(255,255,255,0.82)",
                  border: "1px solid rgba(255,255,255,0.07)",
                }}
              >
                <Icon fontSize="small">close</Icon>
              </IconButton>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            px: 2,
            py: 1.75,
            flex: 1,
            overflowY: "auto",
            background: "linear-gradient(180deg, rgba(255,255,255,0.015), rgba(255,255,255,0))",
          }}
        >
          <Stack spacing={1.5}>
            {(messages.length ? messages : [makeMsg("assistant", "Ask me anything. Navigate anywhere — I’ll keep the conversation.")]).map((m, idx) => (
              <Box key={`${idx}_${m.role}`} sx={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
                <Paper
                  sx={{
                    px: 1.4,
                    py: 1.2,
                    maxWidth: "92%",
                    borderRadius: m.role === "user" ? "22px 22px 8px 22px" : "22px 22px 22px 8px",
                    bgcolor:
                      m.role === "user"
                        ? "linear-gradient(180deg, rgba(64, 132, 255, 0.24), rgba(44, 104, 224, 0.18))"
                        : "rgba(255,255,255,0.05)",
                    background:
                      m.role === "user"
                        ? "linear-gradient(180deg, rgba(74, 144, 255, 0.24), rgba(55, 112, 218, 0.15))"
                        : "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.035))",
                    color: "rgba(255,255,255,0.92)",
                    border: `1px solid ${
                      m.role === "user" ? "rgba(120, 173, 255, 0.24)" : "rgba(255,255,255,0.08)"
                    }`,
                    boxShadow:
                      m.role === "user"
                        ? "0 16px 34px rgba(20, 57, 128, 0.2)"
                        : "0 16px 30px rgba(0, 0, 0, 0.18)",
                  }}
                >
                  <Stack spacing={1}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: 10.5,
                        lineHeight: 1,
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: m.role === "user" ? "rgba(228,238,255,0.72)" : "rgba(255,255,255,0.5)",
                      }}
                    >
                      {m.role === "user" ? "You" : "Assistant"}
                    </Typography>
                    {renderMessageContent(m.content)}
                  </Stack>
                </Paper>
              </Box>
            ))}
            {proposedPatch ? (
              <Paper
                sx={{
                  p: 1.5,
                  borderRadius: 4,
                  border: "1px solid rgba(121, 187, 137, 0.2)",
                  bgcolor: "rgba(26, 43, 32, 0.55)",
                  boxShadow: "0 18px 34px rgba(6, 14, 10, 0.24)",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: 10.5,
                      lineHeight: 1,
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "rgba(216,255,227,0.7)",
                    }}
                  >
                    Patch ready
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontSize: 13, lineHeight: 1.35, color: "#effff3", fontWeight: 600 }}>
                    Review before applying.
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  sx={{ display: "block", fontSize: 12, lineHeight: 1.5, color: "rgba(231,255,237,0.72)", overflowWrap: "anywhere" }}
                >
                  {proposedPatch.summary || ""}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Paper
                    sx={{
                      p: 1.25,
                      borderRadius: 3,
                      bgcolor: "rgba(6, 12, 9, 0.52)",
                      border: "1px solid rgba(255,255,255,0.06)",
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                      fontSize: 11.5,
                      lineHeight: 1.55,
                      whiteSpace: "pre-wrap",
                      color: "rgba(228, 255, 232, 0.86)",
                      maxHeight: 240,
                      overflow: "auto",
                    }}
                  >
                    {JSON.stringify(proposedPatch, null, 2)}
                  </Paper>
                </Box>
              </Paper>
            ) : null}
            <div ref={bottomRef} />
          </Stack>
        </Box>

        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderTop: "1px solid rgba(255,255,255,0.08)",
            background: "linear-gradient(180deg, rgba(11,17,29,0.45), rgba(11,17,29,0.96))",
            backdropFilter: "blur(18px)",
          }}
        >
          {errorText ? (
            <Typography
              variant="caption"
              color="error"
              sx={{
                display: "block",
                mb: 1,
                px: 1.25,
                py: 0.9,
                borderRadius: 2.5,
                bgcolor: "rgba(255, 103, 103, 0.09)",
                border: "1px solid rgba(255, 103, 103, 0.18)",
              }}
            >
              {errorText}
            </Typography>
          ) : null}
          <Paper
            sx={{
              p: 1.2,
              borderRadius: 4,
              bgcolor: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "0 22px 34px rgba(0, 0, 0, 0.22)",
            }}
          >
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={3}
              maxRows={10}
              placeholder={mode ? "Ask for guidance or describe the patch you want..." : "Ask anything..."}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (mode && /propose\s+a\s+patch/i.test(String(input || ""))) propose();
                  else send();
                }
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  alignItems: "flex-start",
                  borderRadius: 3,
                  color: "rgba(255,255,255,0.94)",
                  bgcolor: "rgba(5,10,18,0.34)",
                  "& fieldset": { borderColor: "rgba(255,255,255,0.08)" },
                  "&:hover fieldset": { borderColor: "rgba(131,170,255,0.28)" },
                  "&.Mui-focused fieldset": { borderColor: "rgba(130,176,255,0.46)" },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "rgba(255,255,255,0.42)",
                  opacity: 1,
                },
              }}
            />
            <Box
              sx={{
                mt: 1.1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: "wrap" }}>
                <Chip
                  icon={<Icon sx={{ color: "inherit !important" }}>keyboard_command_key</Icon>}
                  label="Enter to send"
                  size="small"
                  sx={{
                    height: 24,
                    bgcolor: "rgba(255,255,255,0.04)",
                    color: "rgba(255,255,255,0.7)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    "& .MuiChip-label": { px: 1.1, fontSize: 11 },
                  }}
                />
                <Chip
                  label={mode ? `Mode: ${contextLabel}` : "Mode: Chat"}
                  size="small"
                  sx={{
                    height: 24,
                    bgcolor: "rgba(255,255,255,0.04)",
                    color: "rgba(255,255,255,0.7)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    maxWidth: "100%",
                    "& .MuiChip-label": {
                      px: 1.1,
                      fontSize: 11,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
                {mode === "build_sheet" ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={useWebSearch}
                        onChange={(e) => setUseWebSearch(e.target.checked)}
                        sx={{
                          color: "rgba(255,255,255,0.42)",
                          "&.Mui-checked": { color: "#7db6ff" },
                          p: 0.5,
                        }}
                      />
                    }
                    label="Web sources"
                    sx={{
                      m: 0,
                      ml: 0.25,
                      pl: 0.25,
                      borderRadius: 999,
                      color: "rgba(255,255,255,0.72)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      bgcolor: alpha("#FFFFFF", 0.04),
                      "& .MuiFormControlLabel-label": { fontSize: 12.5 },
                    }}
                  />
                ) : null}
              </Stack>
              <Box sx={{ display: "flex", gap: 0.9, justifyContent: "flex-end", flexWrap: "wrap" }}>
                <Button
                  variant="text"
                  startIcon={<Icon>send</Icon>}
                  onClick={send}
                  disabled={loading || proposing || promptEmpty}
                  sx={{
                    ...actionButtonSx,
                    color: "rgba(255,255,255,0.82)",
                    bgcolor: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  {loading ? "Sending..." : "Send"}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Icon>auto_fix_high</Icon>}
                  onClick={mode ? propose : undefined}
                  disabled={!mode || loading || proposing || promptEmpty}
                  sx={{
                    ...actionButtonSx,
                    bgcolor: "linear-gradient(180deg, #8ab7ff 0%, #5c86f8 100%)",
                    background: "linear-gradient(180deg, #89b7ff 0%, #5e86ff 100%)",
                    color: "#07111f",
                    "&:hover": {
                      background: "linear-gradient(180deg, #97c0ff 0%, #6a92ff 100%)",
                    },
                  }}
                >
                  {proposing ? "Proposing..." : "Propose"}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<Icon>done_all</Icon>}
                  onClick={applyHere}
                  disabled={!proposedPatch || !canApplyHere}
                  sx={{
                    ...actionButtonSx,
                    bgcolor: "rgba(102, 208, 138, 0.18)",
                    color: "#dffff0",
                    border: "1px solid rgba(120, 231, 158, 0.22)",
                    "&:hover": {
                      bgcolor: "rgba(102, 208, 138, 0.24)",
                    },
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Paper>
          <Box sx={{ mt: 0.9, display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.44)" }}>
              Shift+Enter adds a new line.
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.44)", fontSize: 11.5, textAlign: "right", overflowWrap: "anywhere" }}
            >
              Ctrl/Cmd+Shift+A toggles the drawer.
            </Typography>
          </Box>
        </Box>
      </Drawer>

      {/* Only show the floating launcher when a dialog is open (avoid covering pagination). */}
      {!open && hasDialogOpen ? (
        <Fab
          color="info"
          size="medium"
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            right: 18,
            bottom: 18,
            zIndex: drawerZ,
            bgcolor: "rgba(19, 31, 58, 0.92)",
            color: "#dfe9ff",
            border: "1px solid rgba(133, 176, 255, 0.28)",
            boxShadow: "0 18px 34px rgba(9, 18, 36, 0.38)",
          }}
          aria-label="Open AI assistant"
        >
          <Icon>smart_toy</Icon>
        </Fab>
      ) : null}
    </>
  );
}

